import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Bookmark, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import DealCard from '../components/DealCard';
import AddPropertyModal from '../components/AddPropertyModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [savedDealIds, setSavedDealIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_properties: 0, states_covered: 0 });
  const [filters, setFilters] = useState({
    propertyType: '',
    damageType: '',
    condition: '',
    region: '',
    city: '',
    minPrice: '',
    maxPrice: ''
  });
  const [cityInput, setCityInput] = useState('');
  const [addressSearch, setAddressSearch] = useState('');

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/deals/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.propertyType) params.append('property_type', filters.propertyType);
      if (filters.damageType) params.append('damage_type', filters.damageType);
      if (filters.condition) params.append('condition', filters.condition);
      if (filters.city) params.append('city', filters.city);
      if (filters.region) params.append('state', filters.region);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (addressSearch) params.append('address', addressSearch);
      
      const response = await axios.get(`${API}/deals?${params.toString()}`);
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals/saved/list`);
      setSavedDealIds(response.data);
    } catch (error) {
      console.error('Error fetching saved deals:', error);
    }
  };

  // Debounce address search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDeals();
    }, 500);
    return () => clearTimeout(timer);
  }, [addressSearch]);

  // Debounce city input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, city: cityInput }));
    }, 500);
    return () => clearTimeout(timer);
  }, [cityInput]);

  useEffect(() => {
    fetchDeals();
    fetchSavedDeals();
    fetchStats();
  }, [filters]);

  const handleToggleSave = async (dealId) => {
    try {
      await axios.post(`${API}/deals/save/${dealId}`);
      await fetchSavedDeals();
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const handlePropertyAdded = (newDeal) => {
    // Add the new deal to the beginning of the list and navigate to its details
    setDeals(prev => [newDeal, ...prev]);
    navigate(`/deal/${newDeal.id}`);
  };

  return (
    <div className="min-h-screen relative z-10 text-zinc-100">
      <header className="border-b border-zinc-800 bg-zinc-950/70 backdrop-blur-md relative z-20">
        <div className="container mx-auto px-4 py-4">
          {/* Logo - Always visible — The Sheffield Organization (black/white) with neon halo */}
          <div className="flex flex-col items-center mb-6">
            <img
              src="/sheffield-logo-transparent.png"
              alt="Sheffield Organization"
              className="sheffield-logo h-28 md:h-32 w-auto"
            />
            <div className="neon-wordmark mt-5" data-testid="neon-wordmark">
              <span className="tsm-prefix">THE</span>
              <span className="tsm-sheffield">SHEFFIELD</span>
              <span className="tsm-org">Organization</span>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 justify-center mb-4">
            <AddPropertyModal onPropertyAdded={handlePropertyAdded} />
            <Button 
              data-testid="underwriter-nav-btn"
              onClick={() => navigate('/underwriter')} 
              className="bg-transparent border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono uppercase tracking-wider rounded-none text-xs md:text-sm"
            >
              <Calculator className="w-4 h-4 mr-1 md:mr-2" />
              Underwriter
            </Button>
            <Button 
              data-testid="saved-deals-nav-btn"
              onClick={() => navigate('/saved')} 
              className="bg-transparent border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono uppercase tracking-wider rounded-none text-xs md:text-sm"
            >
              <Bookmark className="w-4 h-4 mr-1 md:mr-2" />
              Saved ({savedDealIds.length})
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Hero Section for Calculator Tools */}
        <div className="mb-8 text-center py-14 border border-zinc-800 bg-zinc-950/80 backdrop-blur-sm relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, rgba(255,20,147,0.12) 0%, transparent 60%)'
          }}></div>
          <p className="text-xs font-mono uppercase tracking-[0.4em] text-vice-cyan mb-3 neon-cyan">
            — Deal Terminal // v2.0 —
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-widest neon-pink mb-4">
            Real Estate Command
          </h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto font-mono text-sm">
            Professional underwriting tools for analyzing Single Family, Multi-Family, Commercial, NNN, Development deals and more.
          </p>
          <div className="flex justify-center gap-4 flex-wrap relative z-10">
            <Button 
              onClick={() => navigate('/underwriter')}
              className="bg-green-600 hover:bg-green-500 text-black font-mono uppercase tracking-wider rounded-none px-8 py-6"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Open Underwriter
            </Button>
            <AddPropertyModal />
          </div>
        </div>

        {/* Calculator Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="border border-zinc-800 p-6 bg-zinc-950">
            <h3 className="text-sm font-mono uppercase text-green-500 mb-2">Underwriter Tools</h3>
            <p className="text-xs text-zinc-500">Cap Rate, Cash Flow, DSCR, ARV, BRRRR, Pro Forma, Amortization, T12, PFS</p>
          </div>
          <div className="border border-zinc-800 p-6 bg-zinc-950">
            <h3 className="text-sm font-mono uppercase text-green-500 mb-2">Deal Analysis</h3>
            <p className="text-xs text-zinc-500">Single Family, Multi-Family, Commercial, NNN, Development, Portfolio</p>
          </div>
          <div className="border border-zinc-800 p-6 bg-zinc-950">
            <h3 className="text-sm font-mono uppercase text-green-500 mb-2">Financial Planning</h3>
            <p className="text-xs text-zinc-500">Depreciation, Appreciation, CapEx, Net Worth, Bankability Score</p>
          </div>
        </div>

        {/* Manual Properties Section */}
        {stats.total_properties > 0 && (
          <>
            <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
              <h2 className="text-sm font-mono uppercase tracking-widest text-zinc-500">
                Your Properties: {stats.total_properties.toLocaleString()}
              </h2>
              <span className="text-sm font-mono text-zinc-400">
                Showing {deals.length} of {stats.total_properties.toLocaleString()}
              </span>
            </div>

            {loading ? (
              <div data-testid="loading-spinner" className="text-center py-20 font-mono text-zinc-500">LOADING...</div>
            ) : deals.length === 0 ? (
              <div data-testid="no-deals-message" className="text-center py-20">
                <Home className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
                <p className="font-mono text-zinc-500">NO PROPERTIES YET - USE ADD PROPERTY TO ANALYZE DEALS</p>
              </div>
            ) : (
              <div data-testid="deals-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {deals.map((deal) => (
                  <DealCard 
                    key={deal.id} 
                    deal={deal} 
                    isSaved={savedDealIds.includes(deal.id)}
                    onToggleSave={handleToggleSave}
                    onViewDetails={() => navigate(`/deal/${deal.id}`)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}